import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, queryOptions } from "@tanstack/react-query";
import {
  deleteContactMessage,
  listContactMessages,
  setContactMessageArchived,
  setContactMessageRead,
} from "@/lib/contact.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  Inbox,
  Mail,
  MailOpen,
  Search,
  Trash2,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/messages")({
  component: MessagesAdmin,
});

type ContactMessage = {
  id: string;
  form: string;
  full_name: string;
  email: string;
  company: string | null;
  phone: string | null;
  message: string;
  is_read: boolean | number;
  archived_at: string | null;
  created_at: string;
};

/** Short label for the source form; unknown forms keep their stored name. */
const FORM_LABELS: Record<string, string> = {
  "Contact Form": "Contact",
  "AX Incubator Inquiry": "Ax Incubator",
  "Ax Incubator Inquiry": "Ax Incubator",
  "Custom Research Request": "Research",
  "Newsletter Signup": "Newsletter",
};
const formLabel = (form: string) => FORM_LABELS[form] ?? form;

const fmtDate = (iso: string) =>
  new Date(iso.replace(" ", "T") + (iso.endsWith("Z") ? "" : "Z")).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const messagesQueryOptions = () =>
  queryOptions({
    queryKey: ["contact_messages"],
    queryFn: async () => (await listContactMessages()) as ContactMessage[],
  });

type Tab = "inbox" | "unread" | "archived";

function MessagesAdmin() {
  const { data: rows = [] } = useQuery(messagesQueryOptions());
  const qc = useQueryClient();

  const [tab, setTab] = useState<Tab>("inbox");
  const [formFilter, setFormFilter] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["contact_messages"] });

  const readMut = useMutation({
    mutationFn: (p: { id: string; is_read: boolean }) => setContactMessageRead({ data: p }),
    onSuccess: invalidate,
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });
  const archiveMut = useMutation({
    mutationFn: (p: { id: string; archived: boolean }) => setContactMessageArchived({ data: p }),
    onSuccess: (_d, p) => {
      invalidate();
      toast.success(p.archived ? "Archived" : "Restored to inbox");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => deleteContactMessage({ data: { id } }),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(false);
      setOpenId(null);
      toast.success("Message deleted");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const unreadCount = useMemo(
    () => rows.filter((m) => !m.is_read && !m.archived_at).length,
    [rows],
  );
  const archivedCount = useMemo(() => rows.filter((m) => !!m.archived_at).length, [rows]);

  const formOptions = useMemo(() => {
    const seen = new Set<string>();
    rows.forEach((m) => seen.add(m.form));
    return [...seen].sort();
  }, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((m) => {
      if (tab === "archived" ? !m.archived_at : !!m.archived_at) return false;
      if (tab === "unread" && m.is_read) return false;
      if (formFilter && m.form !== formFilter) return false;
      if (
        needle &&
        ![m.full_name, m.email, m.company ?? "", m.message]
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
        return false;
      return true;
    });
  }, [rows, tab, formFilter, q]);

  const open = openId ? rows.find((m) => m.id === openId) ?? null : null;

  /** Open the detail dialog; opening an unread message marks it read. */
  function openMessage(m: ContactMessage) {
    setOpenId(m.id);
    if (!m.is_read) readMut.mutate({ id: m.id, is_read: true });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Messages</CardTitle>
        <CardDescription>
          Submissions from the Contact, Ax Incubator, Research and Newsletter forms.
          {unreadCount > 0 ? ` ${unreadCount} unread.` : " All caught up."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="inbox">Inbox</TabsTrigger>
              <TabsTrigger value="unread">
                Unread{unreadCount > 0 ? ` (${unreadCount})` : ""}
              </TabsTrigger>
              <TabsTrigger value="archived">
                Archived{archivedCount > 0 ? ` (${archivedCount})` : ""}
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative min-w-56 flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, message"
              className="pl-8"
            />
          </div>
        </div>

        {formOptions.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <button
              onClick={() => setFormFilter(null)}
              className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                formFilter === null
                  ? "border-transparent bg-slate-600 text-white"
                  : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              All forms
            </button>
            {formOptions.map((f) => (
              <button
                key={f}
                onClick={() => setFormFilter(formFilter === f ? null : f)}
                className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                  formFilter === f
                    ? "border-transparent bg-slate-600 text-white"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                {formLabel(f)}
              </button>
            ))}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">Received</TableHead>
              <TableHead>From</TableHead>
              <TableHead className="hidden md:table-cell">Form</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="w-24 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((m) => {
              const read = !!m.is_read;
              return (
                <TableRow
                  key={m.id}
                  className="cursor-pointer"
                  onClick={() => openMessage(m)}
                >
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    <span className="flex items-center gap-2">
                      {!read && (
                        <span
                          className="h-2 w-2 shrink-0 rounded-full bg-slate-600"
                          aria-label="Unread"
                        />
                      )}
                      {fmtDate(m.created_at)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className={read ? "text-muted-foreground" : "font-medium"}>
                      {m.full_name}
                    </div>
                    <div className="text-xs text-muted-foreground">{m.email}</div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="secondary">{formLabel(m.form)}</Badge>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div
                      className={`truncate text-sm ${read ? "text-muted-foreground" : ""}`}
                    >
                      {m.message}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={read ? "Mark as unread" : "Mark as read"}
                        title={read ? "Mark as unread" : "Mark as read"}
                        onClick={() => readMut.mutate({ id: m.id, is_read: !read })}
                      >
                        {read ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={m.archived_at ? "Restore to inbox" : "Archive"}
                        title={m.archived_at ? "Restore to inbox" : "Archive"}
                        onClick={() =>
                          archiveMut.mutate({ id: m.id, archived: !m.archived_at })
                        }
                      >
                        {m.archived_at ? (
                          <ArchiveRestore className="h-4 w-4" />
                        ) : (
                          <Archive className="h-4 w-4" />
                        )}
                      </Button>
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                  {tab === "archived"
                    ? "No archived messages."
                    : tab === "unread"
                      ? "No unread messages."
                      : rows.length === 0
                        ? "No messages yet. Submissions from the site's forms appear here."
                        : "No messages match the current filters."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Detail dialog — house standard: centered, sticky tinted header with
          icon + title + context + X, scrollable body of labeled cards, sticky
          footer with destructive actions separated on the left. */}
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpenId(null)}>
        {open && (
          <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
            <div className="sticky top-0 z-10 border-b bg-secondary/60 px-6 py-4 pr-12 backdrop-blur">
              <DialogTitle className="flex items-center gap-2 text-base">
                <Inbox className="h-4 w-4 shrink-0 text-slate-600" />
                Message from {open.full_name}
              </DialogTitle>
              {/* DialogDescription renders a <p>, so only inline elements below. */}
              <DialogDescription className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center rounded-md bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground">
                  {formLabel(open.form)}
                </span>
                <span>{fmtDate(open.created_at)}</span>
                {open.archived_at && (
                  <span className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold">
                    Archived
                  </span>
                )}
              </DialogDescription>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
              <div className="rounded-lg border p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Sender
                </div>
                <dl className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-muted-foreground">Name</dt>
                    <dd>{open.full_name}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Email</dt>
                    <dd>
                      <a href={`mailto:${open.email}`} className="text-primary hover:underline">
                        {open.email}
                      </a>
                    </dd>
                  </div>
                  {open.company && (
                    <div>
                      <dt className="text-xs text-muted-foreground">Company</dt>
                      <dd>{open.company}</dd>
                    </div>
                  )}
                  {open.phone && (
                    <div>
                      <dt className="text-xs text-muted-foreground">Phone</dt>
                      <dd>
                        <a
                          href={`tel:${open.phone.replace(/[^\d+]/g, "")}`}
                          className="text-primary hover:underline"
                        >
                          {open.phone}
                        </a>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
              <div className="rounded-lg border p-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Message
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{open.message}</p>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 flex items-center justify-between gap-2 border-t bg-background px-6 py-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    archiveMut.mutate({ id: open.id, archived: !open.archived_at })
                  }
                >
                  {open.archived_at ? (
                    <>
                      <ArchiveRestore className="h-4 w-4" /> Restore
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4" /> Archive
                    </>
                  )}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => readMut.mutate({ id: open.id, is_read: !open.is_read })}
                >
                  {open.is_read ? (
                    <>
                      <Mail className="h-4 w-4" /> Mark unread
                    </>
                  ) : (
                    <>
                      <MailOpen className="h-4 w-4" /> Mark read
                    </>
                  )}
                </Button>
                <Button size="sm" onClick={() => setOpenId(null)}>
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this message?</AlertDialogTitle>
            <AlertDialogDescription>
              The message from {open?.full_name ?? "this sender"} will be permanently deleted.
              This cannot be undone — archiving keeps it retrievable instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => open && delMut.mutate(open.id)}
            >
              Delete message
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
